import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { Link, useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import {
  ImageBackground,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';
import { validateEmail, validatePassword } from '@/lib/validation';

const wallpaper = require('../../../assets/wallpaper.jpg');

const GOLD = '#C9A843';
const CARD_BG = 'rgba(13, 27, 42, 0.84)';
const CARD_BORDER = 'rgba(201, 168, 67, 0.22)';
const TEXT_WHITE = '#FFFFFF';
const TEXT_MUTED = 'rgba(255,255,255,0.58)';

type FieldErrors = { email?: string; code?: string; password?: string; confirm?: string };

export default function ResetPasswordScreen() {
  const { t } = useTranslation();
  const {
    passwordRecovery,
    confirmPasswordReset,
    setNewPassword,
    clearPasswordRecovery,
    requestPasswordReset,
  } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const params = useLocalSearchParams<{ email?: string }>();

  const linkMode = passwordRecovery;

  const [email, setEmail] = useState(params.email ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  async function onSubmit() {
    setFormError(null);
    const next: FieldErrors = {};
    if (!linkMode) {
      next.email = validateEmail(email) ?? undefined;
      if (!code.trim()) next.code = t('auth.reset_password.code_error');
    }
    next.password = validatePassword(password) ?? undefined;
    if (!next.password && confirm !== password) {
      next.confirm = t('errors.passwordMismatch');
    }
    if (next.email || next.code || next.password || next.confirm) {
      setErrors(next);
      return;
    }
    setErrors({});

    setLoading(true);
    const { error } = linkMode
      ? await setNewPassword(password)
      : await confirmPasswordReset(email, code, password);
    setLoading(false);

    if (error) {
      setFormError(error);
      return;
    }
    clearPasswordRecovery();
    appAlert(t('auth.reset_password.success'));
  }

  async function onResend() {
    const err = validateEmail(email);
    if (err) {
      setErrors((e) => ({ ...e, email: err }));
      return;
    }
    setResending(true);
    const { error } = await requestPasswordReset(email);
    setResending(false);
    appAlert(
      error ? t('common.error') : t('auth.reset_password.resent_title'),
      error ?? t('auth.reset_password.resent_body'),
    );
  }

  return (
    <ImageBackground source={wallpaper} style={styles.flex} resizeMode="cover">
      <View style={styles.overlay} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[
            styles.content,
            { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.lg },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.card}>
            <View style={styles.header}>
              <Text style={styles.brand}>{t('auth.reset_password.title')}</Text>
              <Text style={styles.subtitle}>
                {linkMode
                  ? t('auth.reset_password.subtitle_link')
                  : t('auth.reset_password.subtitle_code')}
              </Text>
            </View>

            <View style={styles.form}>
              {!linkMode && (
                <>
                  <Input
                    label="Email"
                    value={email}
                    onChangeText={setEmail}
                    error={errors.email}
                    placeholder="name@example.com"
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="email"
                    textContentType="emailAddress"
                  />
                  <Input
                    label={t('auth.reset_password.code_label')}
                    value={code}
                    onChangeText={setCode}
                    error={errors.code}
                    placeholder={t('placeholder.otp')}
                    keyboardType="number-pad"
                    autoCapitalize="none"
                    textContentType="oneTimeCode"
                    maxLength={6}
                  />
                </>
              )}
              <Input
                label={t('auth.reset_password.new_password')}
                value={password}
                onChangeText={setPassword}
                error={errors.password}
                placeholder={t('placeholder.passwordNew')}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                autoComplete="password-new"
                textContentType="newPassword"
                rightElement={
                  <Pressable onPress={() => setShowPassword((v) => !v)} hitSlop={8}>
                    <Ionicons name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={20} color={TEXT_MUTED} />
                  </Pressable>
                }
              />
              <Input
                label={t('auth.reset_password.confirm_label')}
                value={confirm}
                onChangeText={setConfirm}
                error={errors.confirm}
                placeholder={t('placeholder.passwordConfirm')}
                secureTextEntry={!showConfirm}
                autoCapitalize="none"
                rightElement={
                  <Pressable onPress={() => setShowConfirm((v) => !v)} hitSlop={8}>
                    <Ionicons name={showConfirm ? 'eye-off-outline' : 'eye-outline'} size={20} color={TEXT_MUTED} />
                  </Pressable>
                }
              />

              {!!formError && <Text style={styles.formError}>{formError}</Text>}

              <Button title={t('auth.reset_password.change_btn')} onPress={onSubmit} loading={loading} />
              {!linkMode && (
                <Button
                  title={resending ? t('auth.reset_password.sending') : t('auth.reset_password.resend_btn')}
                  variant="secondary"
                  onPress={onResend}
                  loading={resending}
                />
              )}
            </View>

            <View style={styles.footer}>
              <Text style={styles.footerText}>{t('common.backTo')} </Text>
              <Link href="/(auth)/login" style={styles.footerLink}>
                {t('auth.action.login')}
              </Link>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13, 27, 42, 0.68)',
  },
  content: {
    // Σε μεγάλες οθόνες η φόρμα μένει κεντραρισμένη σε αναγνώσιμο πλάτος
    // αντί να τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
    width: '100%',
    maxWidth: contentMaxWidth.form,
    alignSelf: 'center',
    flexGrow: 1,
    paddingHorizontal: spacing.lg,
    gap: spacing.xl,
    justifyContent: 'center',
  },
  card: {
    backgroundColor: CARD_BG,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    padding: spacing.lg,
    gap: spacing.lg,
  },
  header: {
    gap: spacing.sm,
  },
  brand: {
    color: TEXT_WHITE,
    fontSize: fontSize.xl,
    fontWeight: '700',
  },
  subtitle: {
    color: TEXT_MUTED,
    fontSize: fontSize.md,
    lineHeight: 22,
  },
  form: {
    gap: spacing.md,
  },
  formError: {
    color: '#F87171',
    fontSize: fontSize.sm,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {
    color: TEXT_MUTED,
    fontSize: fontSize.md,
  },
  footerLink: {
    color: GOLD,
    fontSize: fontSize.md,
    fontWeight: '600',
  },
});
